import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMembership } from '../../contexts/MembershipContext';
import MembershipTierCard from '../../components/membership/MembershipTierCard';
import TestimonialCard from '../../components/membership/TestimonialCard';
import FAQSection from '../../components/membership/FAQSection';
import TokenPurchaseSection from '../../components/membership/TokenPurchaseSection';
import MembershipManagement from '../../components/membership/MembershipManagement';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';

const TIER_COLORS = {
  community: '#f5f5f5',
  gold: '#FFD700',
  platinum: '#E5E4E2'
};

const TESTIMONIALS = [
  {
    name: 'Sarah Johnson',
    role: 'Software Engineer',
    testimonial:
      'The Gold membership helped me land interviews at top tech companies. The credits system makes it easy to use services when I need them most!',
    rating: 5,
    tier: 'gold'
  },
  {
    name: 'Michael Chen',
    role: 'Marketing Director',
    testimonial:
      'Platinum membership is worth every penny. With 1000 credits monthly, I can use all the premium features without worrying about running out.',
    rating: 5,
    tier: 'platinum'
  }
];

const FAQS = [
  {
    question: 'What are credits?',
    answer:
      'Credits are our virtual currency that you can spend on various services like resume tailoring, job matching, resume analysis, and more.'
  },
  {
    question: 'How often do credits reset?',
    answer:
      'Paid plans receive a fresh batch of credits every billing period (monthly or quarterly). The Community plan gives you a one-time lifetime credit grant.'
  },
  {
    question: 'How many credits do services cost?',
    answer:
      'Different services cost different amounts of credits. Job Tailor costs 10 credits, Job Matching costs 5 credits, Resume Massage analysis costs 5 credits, Resume Massage Polish costs 10 credits, Resume Upload (beyond the first) costs 2 credits, and Interview question preparation costs 5 credits.'
  },
  {
    question: 'Can I purchase additional tokens?',
    answer:
      'Yes, you can purchase additional tokens at any time. We offer various packages starting from $1.99 for 20 tokens, with bigger packages offering better savings.'
  },
  {
    question: 'Can I cancel my membership?',
    answer:
      'Yes. When you cancel, you keep your current plan and credits until the end of the paid period. After that, your account is automatically moved back to the Community plan.'
  }
];

const MembershipComparison = () => {
  const { membershipConfig, currentMembership, startCheckout, refreshMembership, loading } = useMembership();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [interval, setBillingInterval] = useState('monthly');
  const location = useLocation();
  const navigate = useNavigate();

  // Show toast on return from Stripe Checkout and clean the query string.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get('status');
    if (!checkoutStatus) return;

    if (checkoutStatus === 'success') {
      toast.success('Payment successful! Your membership is being activated...');
      refreshMembership();
    } else if (checkoutStatus === 'cancel') {
      toast.info('Checkout was cancelled. No charges were made.');
    }

    // Strip the query params so reloading the page doesn't re-fire the toast.
    params.delete('status');
    params.delete('session_id');
    navigate(
      { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, refreshMembership]);

  const currentTier = useMemo(
    () => String(currentMembership?.type || '').toLowerCase(),
    [currentMembership]
  );

  const handleUpgrade = async (tierName) => {
    const normalizedTier = String(tierName || '').toLowerCase();

    if (normalizedTier === 'community') {
      toast.info('Community is your default plan. Cancel any active subscription to switch back.');
      return;
    }

    if (normalizedTier === currentTier && currentMembership?.hasActiveSubscription) {
      toast.info('You are already on this plan');
      return;
    }

    setIsUpgrading(true);
    try {
      const result = await startCheckout(normalizedTier, interval);
      if (!result.success) {
        const detail =
          result.error?.response?.data?.detail ||
          'Failed to start checkout. Please try again.';
        toast.error(detail);
      }
      // On success, startCheckout already redirects to Stripe.
    } catch (error) {
      console.error('Error starting checkout:', error);
      toast.error('An error occurred while starting checkout.');
    } finally {
      setIsUpgrading(false);
    }
  };

  if (loading || !membershipConfig) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const buttonLabelFor = (tierName) => {
    const t = tierName.toLowerCase();
    if (t === 'community') return 'Current Plan';
    if (t === currentTier && currentMembership?.hasActiveSubscription) return 'Current Plan';
    return interval === 'quarterly' ? 'Upgrade (Quarterly)' : 'Upgrade (Monthly)';
  };

  const isCurrent = (tierName) => {
    const t = tierName.toLowerCase();
    if (t === 'community') return currentTier === 'community';
    return t === currentTier && currentMembership?.hasActiveSubscription;
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-12">
        <PageHeader
          title={pageDescriptions['/membership'].title}
          description={pageDescriptions['/membership'].description}
          whatYouCanDo={pageDescriptions['/membership'].whatYouCanDo}
          whatYouShouldDo={pageDescriptions['/membership'].whatYouShouldDo}
        />
      </div>

      {/* Current subscription block + history tabs */}
      <MembershipManagement />

      {/* Limited-time sale callout */}
      <div className="mb-8 rounded-xl bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 text-white px-5 py-4 shadow-md flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-block bg-white text-rose-600 text-xs font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full">
            Limited Time
          </span>
          <p className="font-semibold text-base md:text-lg">
            Launch sale &mdash; save up to 25% with quarterly billing on Gold &amp; Platinum.
          </p>
        </div>
        <p className="text-xs md:text-sm opacity-90">Offer ends soon. Lock in early-adopter pricing.</p>
      </div>

      {/* Billing interval toggle */}
      <div className="mb-6 flex items-center justify-center">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setBillingInterval('monthly')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              interval === 'monthly'
                ? 'bg-primary-600 text-white shadow'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval('quarterly')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              interval === 'quarterly'
                ? 'bg-primary-600 text-white shadow'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Quarterly <span className="ml-1 text-xs font-normal opacity-80">save up to 25%</span>
          </button>
        </div>
      </div>

      {/* Membership tiers */}
      <div className="grid md:grid-cols-3 gap-8 mb-16">
        {/* Community Tier */}
        <MembershipTierCard
          tier="Community"
          monthlyPrice={0}
          quarterlyPrice={0}
          totalCredits={66}
          frequency="Life Time"
          features={[
            'Job Tailor',
            'Job Matching',
            'Multiple resume uploads',
            'Access to community features'
          ]}
          color={TIER_COLORS.community}
          isPopular={false}
          selectedInterval={interval}
          isCurrentPlan={isCurrent('community')}
          buttonLabel={buttonLabelFor('Community')}
          onUpgrade={() => handleUpgrade('community')}
        />

        {/* Gold Tier */}
        <MembershipTierCard
          tier="Gold"
          monthlyPrice={19.99}
          quarterlyPrice={47.98}
          quarterlySavingsPercent={20}
          totalCredits={500}
          frequency="Monthly"
          features={[
            'Job Tailor',
            'Job Matching',
            'Resume analysis',
            'Resume polish',
            'Multiple resume uploads',
            'Interview question prep',
            'Priority support'
          ]}
          color={TIER_COLORS.gold}
          isPopular={true}
          saleTag="Limited Sale"
          selectedInterval={interval}
          isCurrentPlan={isCurrent('gold')}
          buttonLabel={buttonLabelFor('Gold')}
          buttonDisabled={isUpgrading}
          onUpgrade={() => handleUpgrade('gold')}
        />

        {/* Platinum Tier */}
        <MembershipTierCard
          tier="Platinum"
          monthlyPrice={24.99}
          quarterlyPrice={56.23}
          quarterlySavingsPercent={25}
          totalCredits={1000}
          frequency="Monthly"
          features={[
            'Job Tailor',
            'Job Matching',
            'Resume analysis',
            'Resume polish',
            'Multiple resume uploads',
            'Interview question prep',
            '24/7 priority support',
            'Monthly career coaching session'
          ]}
          color={TIER_COLORS.platinum}
          isPopular={false}
          saleTag="Limited Sale"
          selectedInterval={interval}
          isCurrentPlan={isCurrent('platinum')}
          buttonLabel={buttonLabelFor('Platinum')}
          buttonDisabled={isUpgrading}
          onUpgrade={() => handleUpgrade('platinum')}
        />
      </div>

      {/* Token Purchase Section */}
      <TokenPurchaseSection />

      {/* Testimonials */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-center mb-8">What Our Members Say</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              name={testimonial.name}
              role={testimonial.role}
              testimonial={testimonial.testimonial}
              rating={testimonial.rating}
              tierColor={TIER_COLORS[testimonial.tier]}
            />
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <FAQSection faqs={FAQS} />
    </div>
  );
};

export default MembershipComparison;
